package modysy.tools

import modysy.datastructures.Vector2
import modysy.view.Color
import org.scalajs.dom

/** A class adding a toolbox with several tools to the game.
  * Methods for rendering and reaction on user input must be called
  * from outside this class.
  *
  * The toolbox provides mouse and touch support.
  */
class Toolbox(energyStorage: EnergyStorage) {

  private val TOOLBOX_SIZE = 10 // can be used to change toolbox size

  private var tools: List[Tool] = List()
  private var selectedToolIndex: Int = -1

  // previous touch position while dragging current tool
  private var touchDragPreviousWorldPos: Option[Vector2] = None

  // start touch position while placing current tool
  private var touchPlaceStartScreenPos: Option[Vector2] = None

  // the amount of pixel a touch can move until it is not considered at the same position any longer
  // used to determine if a touch is the 'place tool' gesture or a 'movement tool' gesture
  private final val TOUCH_PLACE_MOVE_THRESHOLD = 10

  /** Renders the toolbox and any tool icons added onto the given context
    * using the given optional mouse cursor position to handle mouse hovering.
    *
    * Pass None as mouse position when using touch interactions.
    *
    * @param ctx            rendering context to draw on
    * @param mousePosCanvas canvas position of the cursor relative to the upper left corner of the canvas
    */
  def renderStatics(
      ctx: dom.CanvasRenderingContext2D,
      mousePosCanvas: Option[Vector2]
  ): Unit = {
    if (tools.isEmpty) return

    // draw toolbox background
    ctx.fillStyle = Color.Brown
    ctx.fillRect(
      0,
      0,
      TOOLBOX_SIZE + tools.length * 6 * TOOLBOX_SIZE,
      TOOLBOX_SIZE * 7
    )

    // draw highlighted background for the selected tool
    if (selectedToolIndex > -1 && tools(selectedToolIndex).cost <= energyStorage.balance) {
      ctx.fillStyle = Color.Red_LightLight
      ctx.fillRect(
        selectedToolIndex * 6 * TOOLBOX_SIZE + TOOLBOX_SIZE / 2,
        TOOLBOX_SIZE / 2,
        TOOLBOX_SIZE * 6,
        TOOLBOX_SIZE * 6
      )
    }

    // draw highlighted background for the hovered tool
    val hoveredToolIndex = toolAt(mousePosCanvas)
    if (hoveredToolIndex > -1 && tools(hoveredToolIndex).cost <= energyStorage.balance) {
      ctx.fillStyle = Color.Red_Light
      ctx.fillRect(
        hoveredToolIndex * 6 * TOOLBOX_SIZE + TOOLBOX_SIZE / 2,
        TOOLBOX_SIZE / 2,
        TOOLBOX_SIZE * 6,
        TOOLBOX_SIZE * 6
      )
    }

    // draw icons with backgrounds and cost
    ctx.save()
    ctx.translate(TOOLBOX_SIZE, TOOLBOX_SIZE)
    tools.foreach(t => {
      if (t.cost <= energyStorage.balance) {
        ctx.fillStyle = Color.Yellow_Brown
      } else {
        ctx.fillStyle = Color.Grey
      }
      ctx.fillRect(0, 0, 5 * TOOLBOX_SIZE, 5 * TOOLBOX_SIZE)

      ctx.translate(2.5 * TOOLBOX_SIZE, 2.5 * TOOLBOX_SIZE)
      t.renderIcon(ctx)

      ctx.font = "15px Arial"
      ctx.fillStyle = Color.White
      ctx.textAlign = "center"
      ctx.fillText(t.uses.toString, 0, 3 * TOOLBOX_SIZE, 5 * TOOLBOX_SIZE)

      ctx.translate(3.5 * TOOLBOX_SIZE, -2.5 * TOOLBOX_SIZE)
    })
    ctx.restore()
  }

  /** Renders the non-icon part of any selected tool onto the given context
    * using the given optional mouse cursor position to handle mouse hovering.
    *
    * Pass None as mouse position when using touch interactions.
    *
    * @param ctx            rendering context to draw on
    * @param mousePosCanvas canvas position of the cursor relative to the upper left corner of the canvas
    */
  def render(
      ctx: dom.CanvasRenderingContext2D,
      mousePosCanvas: Option[Vector2]
  ): Unit = {
    if (selectedToolIndex > -1 && !isOverToolbox(mousePosCanvas) && tools(
          selectedToolIndex
        ).cost <= energyStorage.balance) {
      tools(selectedToolIndex).render(ctx)
    }
  }

  /** Add a tool to the toolbox
    *
    * @param tool the tool to be added
    */
  def addTool(tool: Tool): Unit = {
    tools = tool :: tools
  }

  /** Call this when the primary mouse button is clicked on the canvas.
    *
    * Any tool under the given mouse screen position gets selected and
    * any tool selected before becomes unselected.
    *
    * When a tool was selected before and no new tool is selected,
    * then a click into the game world uses the selected tool as long
    * enough energy is available. When using the tool energy is removed and
    * the tool gets unselected.
    *
    * @param mousePosCanvas canvas position of the cursor relative to the upper left corner of the canvas
    * @param mousePosWorld  world position of the cursor relative to the upper left corner of the world
    */
  def onMousePrimaryButtonClick(
      mousePosCanvas: Vector2,
      mousePosWorld: Vector2
  ): Unit = {
    val hoveredToolIndex = toolAt(mousePosCanvas)
    if (hoveredToolIndex > -1 && tools(hoveredToolIndex).uses > 0) {

      if (selectedToolIndex > -1) {
        tools(selectedToolIndex).moveReset() // reset previous selected tool
      }

      selectedToolIndex = hoveredToolIndex
    } else {
      if (selectedToolIndex > -1 && !isOverToolbox(mousePosCanvas) && tools(
            selectedToolIndex
          ).uses > 0) {
        tools(selectedToolIndex).moveTo(mousePosWorld) // always use tool at mouse pointer location,
        // even if the tool was positioned elsewhere using touch
        if (tools(selectedToolIndex).use()) {
          tools(selectedToolIndex).uses -= 1
          if (tools(selectedToolIndex).uses < 1) {
            tools(selectedToolIndex).moveReset()
            selectedToolIndex = -1
          } else {
            tools(selectedToolIndex).moveTo(mousePosWorld)
          }
        }
      }
    }
  }

  /** Call this to activate the tool at toolIndex
    * needs mouse to set activation position
    * this only toggles tools on or off, you still need to click to set the tool
    *
    * @param toolIndex
    * @param mouse
    */
  def activateTool(toolIndex: Int, mouse: Vector2): Unit = {
    if (toolIndex < tools.length) {
      val hoveredToolIndex = toolIndex
      println(tools(hoveredToolIndex).uses)
      if (hoveredToolIndex > -1 && tools(hoveredToolIndex).uses > 0) {

        if (selectedToolIndex > -1) {
          tools(selectedToolIndex).moveReset() // reset previous selected tool
        }
        selectedToolIndex = hoveredToolIndex
        tools(selectedToolIndex).moveTo(mouse)
      }
    }
  }

  /** Call when the user moves the mouse over the canvas.
    *
    * If any tool is selected, then it is moved to the current mouse position.
    *
    * @param mousePosWorld world position of the cursor relative to the upper left corner of the world
    */
  def onMouseMove(mousePosWorld: Vector2): Unit = {
    if (selectedToolIndex > -1) {
      tools(selectedToolIndex).moveTo(mousePosWorld)
    }
  }

  /** Call this when the user starts to touch and touches the canvas at only one spot simultaneously.
    *
    * If any tool is selected, then a touch onto the tool will enable tool movement by
    * moving the touched spot.
    *
    * A touch to the toolbox will select any touched tool and
    * any tool selected before becomes unselected. Selecting a PlacementTool will
    * move the tool into the game world at the given screenCenter to make dragging possible.
    *
    * Returns true if the touch gesture has hit anything and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * @param touchCanvasPos canvas position of the starting touch relative to the upper left corner of the canvas
    * @param touchWorldPos  world position of the starting touch relative to the upper left corner of the world
    * @param screenCenter   world position of the middle of the current screen relative to the upper left corner of the world
    * @return true, if the touch event can be considered as consumed
    */
  def onTouchSingleStart(
      touchCanvasPos: Vector2,
      touchWorldPos: Vector2,
      screenCenter: Vector2
  ): Boolean = {
    val hoveredToolIndex = toolAt(touchCanvasPos)
    if (hoveredToolIndex > -1 && tools(hoveredToolIndex).cost <= energyStorage.balance) {

      if (selectedToolIndex > -1) {
        tools(selectedToolIndex).moveReset() // reset previous selected tool
      }

      selectedToolIndex = hoveredToolIndex

      // placement tools are positioned to enable repositioning by dragging
      // non placement tools are positioned only on touch at the touched position
      if (tools(selectedToolIndex).isInstanceOf[PlacementTool]) {
        tools(selectedToolIndex).moveTo(screenCenter)
      }

      return true
    } else {
      if (selectedToolIndex > -1 && isOverTool(touchWorldPos)) {
        touchDragPreviousWorldPos = Some(touchWorldPos)
        touchPlaceStartScreenPos = Some(touchCanvasPos)
      }
    }
    false
  }

  /** Call this when the user moves his touch and touches to the canvas at only one spot simultaneously.
    *
    * If any placement tool is selected and was touched, then it will be moved according to the
    * given touched world position.
    *
    * Moving a tool over some distance will prevent tool usage with this touch.
    *
    * Returns true if the touch gesture has moved any movable tool selected and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * @param touchCanvasPos canvas position of the moving touch relative to the upper left corner of the canvas
    * @param touchWorldPos  world position of the moving touch relative to the upper left corner of the world
    * @return true, if the touch event can be considered as consumed
    */
  def onTouchSingleMove(
      touchCanvasPos: Vector2,
      touchWorldPos: Vector2
  ): Boolean = {
    if (selectedToolIndex > -1) {
      if (tools(selectedToolIndex).isInstanceOf[PlacementTool]) {
        if (touchDragPreviousWorldPos.isDefined) {

          if (!isOverToolbox(touchCanvasPos)) { // prevent dragging over the toolbox
            tools(selectedToolIndex).moveBy(
              touchWorldPos - touchDragPreviousWorldPos.get
            )
            touchDragPreviousWorldPos = Some(touchWorldPos)
          }

          // prevent any tool placement if touch moved a lot
          if (touchPlaceStartScreenPos.isDefined) {
            if ((touchPlaceStartScreenPos.get - touchCanvasPos).norm > TOUCH_PLACE_MOVE_THRESHOLD) {
              touchPlaceStartScreenPos = None
            }
          }

          return true
        }
      }
    }

    false
  }

  /** Call this when the user ends a touch the canvas at only one spot simultaneously.
    *
    * If any placement tool is selected, then a touch starting and ending on the tool without having moved the touch a lot
    * will use the tool as long enough energy is available. When using the tool energy is removed and
    * the tool gets unselected.
    *
    * If any non placement tool is selected, then a touch at the game world will use the tool as long enough
    * energy is available. When using the tool energy is removed and the tool gets unselected.
    *
    * Returns true if the touch gesture has hit anything and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * @param touchCanvasPos canvas position of the ending touch relative to the upper left corner of the canvas
    * @param touchWorldPos  world position of the ending touch relative to the upper left corner of the world
    * @return true, if the touch event can be considered as consumed
    */
  def onTouchSingleEnd(
      touchCanvasPos: Vector2,
      touchWorldPos: Vector2
  ): Boolean = {
    if (selectedToolIndex > -1) {
      if (tools(selectedToolIndex).isInstanceOf[PlacementTool]) {
        if (touchPlaceStartScreenPos.isDefined) {
          if (!isOverToolbox(touchCanvasPos) && tools(selectedToolIndex).cost <= energyStorage.balance) {
            if (tools(selectedToolIndex).use()) {
              energyStorage.removeEnergy(tools(selectedToolIndex).cost)
              tools(selectedToolIndex).moveReset()
              selectedToolIndex = -1
            }
          }

          touchDragPreviousWorldPos = None
          return true
        }
      } else {
        if (!isOverToolbox(touchCanvasPos) && tools(selectedToolIndex).cost <= energyStorage.balance) {
          tools(selectedToolIndex).moveTo(touchWorldPos)
          if (tools(selectedToolIndex).use()) {
            energyStorage.removeEnergy(tools(selectedToolIndex).cost)
            tools(selectedToolIndex).moveReset()
            selectedToolIndex = -1
          } else {
            tools(selectedToolIndex).moveReset()
          }
        }
      }
    }

    touchDragPreviousWorldPos = None
    false
  }

  /** Returns true, if the given world position relative to the upper left corner of the
    * game world is over any selected placement tool. Returns false otherwise.
    *
    * @param worldPos world position relative to the upper left corner of the world
    * @return True, if any selected placement tool is at given position, otherwise false.
    */
  private def isOverTool(worldPos: Vector2): Boolean = {
    tools(selectedToolIndex) match {
      case tool: PlacementTool => tool.isOver(worldPos)
      case _                   => false
    }
  }

  /** Returns true if the given canvas position is over the toolbox, otherwise false.
    *
    * @param canvasPos canvas position relative to the upper left corner of the canvas
    * @return True, if the given position is over the toolbox, false otherwise
    */
  private def isOverToolbox(canvasPos: Vector2): Boolean = {
    canvasPos.y < TOOLBOX_SIZE * 7 && canvasPos.x < TOOLBOX_SIZE + tools.length * 6 * TOOLBOX_SIZE && tools.nonEmpty
  }

  /** Returns true if the given optional canvas position is defined and over the toolbox,
    * otherwise false.
    *
    * @param canvasPos canvas position relative to the upper left corner of the canvas
    * @return True, if the given position is defined and over the toolbox, false otherwise
    */
  private def isOverToolbox(canvasPos: Option[Vector2]): Boolean = {
    if (canvasPos.isEmpty) return false
    isOverToolbox(canvasPos.get)
  }

  /** Returns the index of the tool at the given canvas position in the tools list
    * or -1 if no tool under the given position
    *
    * @param canvasPos canvas position relative to the upper left corner of the canvas
    * @return list index of tool at the given position
    */
  private def toolAt(canvasPos: Vector2): Int = {
    if (tools.isEmpty) return -1

    if (canvasPos.y > TOOLBOX_SIZE && canvasPos.y < 6 * TOOLBOX_SIZE) {
      if (canvasPos.x > 0) {
        if (canvasPos.x < (tools.length * 6 * TOOLBOX_SIZE)) {
          if (!(0 < (canvasPos.x % (6 * TOOLBOX_SIZE)) && (canvasPos.x % (6 * TOOLBOX_SIZE)) < TOOLBOX_SIZE)) {
            return ((canvasPos.x - (canvasPos.x % (6 * TOOLBOX_SIZE))) / (6 * TOOLBOX_SIZE)).toInt
          }
        }
      }
    }

    -1
  }

  /** Returns the index of the tool at the given optional canvas position in the tools list
    * or -1 if canvasPos is None or no tool under the given position.
    *
    * @param canvasPos canvas position relative to the upper left corner of the canvas
    * @return list index of tool at the given position
    */
  private def toolAt(canvasPos: Option[Vector2]): Int = {
    if (canvasPos.isEmpty) return -1
    toolAt(canvasPos.get)
  }

  def resetToolbox(): Unit = {
    tools = List()
  }

  def getToolBoxAt(index: Int): Tool = {
    return tools(index)
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/tools/Toolbox.scala